import React from 'react';
import { Box, Divider } from '@mui/material';
import { TimeRange } from '@uplift/types';
import { useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';
import { ModalContent, ModalTitle } from '../../../Modal';
import { ModalFullPage, ModalFullPageActions } from '../../../ModalFullPage';
import { AvailabilityForm } from './AvailabilityForm.component';
import { ResetOrRemoveAvailabilityButton } from './AvailabilityFormButtons.component';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { CancelButton, SubmitButton } from '../../../Form';
import { Button } from '../../../Button';
import { AvailabilityFormValues } from './AvailabilityForm.types';


export type AvailabilityFormSingleDateBodyProps = {
  defaultAvailability?: TimeRange[];
  warningOnLessThenHour?: boolean;
  handleClose: () => void;
  prepend?: React.ReactNode;
  isAddingOverride: boolean;
  onError?: (err: string) => void;
};


export const AvailabilityFormSingleDateBodyDesktop: React.FC<AvailabilityFormSingleDateBodyProps> = (props) => {
  const l10n = useL10n();
  const isMobile = useIsMobile();

  return (
    <>
      <ModalContent dividers>
        <Box>
          {props.prepend}
          <Box maxHeight={isMobile ? undefined : '296px'} overflow="auto">
            <AvailabilityForm
              warningOnLessThenHour={props.warningOnLessThenHour}
              isMobile={isMobile}
            />
          </Box>
        </Box>
      </ModalContent>
      <ModalFullPageActions>
        <Box display="flex" justifyContent="space-between" width="100%" boxSizing="border-box">
          <Box>
            <ResetOrRemoveAvailabilityButton
              defaultAvailability={props.defaultAvailability}
              isAddingOverride={props.isAddingOverride}
            />
          </Box>
          <Box display="flex">
            <CancelButton size="medium" onClick={props.handleClose}>{l10n.general.cancel}</CancelButton>
            <SubmitButton size="medium" color="secondary">{l10n.general.save}</SubmitButton>
          </Box>
        </Box>
      </ModalFullPageActions>
    </>
  );
};


const ResetAvailabilityButton: React.FC<{
  onError?: (err: string) => void;
  defaultAvailability?: TimeRange[];
}> = (props) => {
  const l10n = useL10n();
  const formikContext = useFormikContext<AvailabilityFormValues>();
  const defaultTheSameAsCurrent = isEqual(
    formikContext.values.availability.map(s => [ s.start, s.end ]),
    props.defaultAvailability?.map(s => [ s.start, s.end ]) ?? [],
  );

  return (
    <Button
      fullWidth
      className={defaultTheSameAsCurrent && 'Mui-disabled'}
      style={{ pointerEvents: 'initial' }}
      onClick={() => {
        if (defaultTheSameAsCurrent) {
          props.onError?.('This is already your recurring availability');
        } else {
          const newAvailability = (props.defaultAvailability ?? []).map((r, i) => ({ id: Date.now() + i, ...r }));

          formikContext.setValues({ availability: newAvailability });
        }
      }}
    >
      {l10n.calendar.availability.editForm.revertToRecurring}
    </Button>
  );
};


export const AvailabilityFormSingleDateBodyMobile: React.FC<AvailabilityFormSingleDateBodyProps> = (props) => {
  const l10n = useL10n();

  return (
    <>
      <ModalContent dividers noPadding>
        <Box>
          {props.prepend}
          <Box overflow="auto">
            <Box p={2}>
              <AvailabilityForm
                warningOnLessThenHour={props.warningOnLessThenHour}
                isMobile
              />
            </Box>
            <Divider />
            <Box p={2}>
              <ResetAvailabilityButton
                defaultAvailability={props.defaultAvailability}
                onError={props.onError}
              />
            </Box>
          </Box>
        </Box>
      </ModalContent>
      <ModalFullPageActions>
        <SubmitButton fullWidth size="medium" color="secondary">{l10n.general.save}</SubmitButton>
      </ModalFullPageActions>
    </>
  );
};


export type AvailabilityFormSingleDateDialogProps = AvailabilityFormSingleDateBodyProps & {
  open: boolean;
  title: string;
  handleClosed?: () => void;
};


export const AvailabilityFormSingleDateDialog: React.FC<AvailabilityFormSingleDateDialogProps> = (props) => {
  const isMobile = useIsMobile();
  const Component = isMobile ? AvailabilityFormSingleDateBodyMobile : AvailabilityFormSingleDateBodyDesktop;

  return (
    <ModalFullPage
      open={props.open}
      onClose={props.handleClose}
      onExited={props.handleClosed}
      mobileOnly
      transition
      maxWidth="xs"
    >
      <ModalTitle onClose={props.handleClose} className="hello">
        {props.title}
      </ModalTitle>
      <Component {...props} />
    </ModalFullPage>
  );
};
